import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { createClinicalNote } from "../services/clinicalNotes"; 
import { useAuth } from "../contexts/AuthContext";
import { ROLES } from "../constants/roles"; 
import { toast } from "react-toastify";
import CustomSelect from "../components/Common/CustomSelect";

const AddClinicalNote: React.FC = () => {
  const { id: patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation() as any;

  const { user } = useAuth();

  const [title, setTitle] = useState("");
  const [typeText, setTypeText] = useState("");
  const [content, setContent] = useState("");
  const [entryMode, setEntryMode] = useState<"manual" | "upload">("manual");

  const [noteFile, setNoteFile] = useState<File | null>(null);

  // Structured fields
  const [histPresenting, setHistPresenting] = useState("");
  const [pastMedical, setPastMedical] = useState("");
  const [medication, setMedication] = useState("");
  const [familyHistory, setFamilyHistory] = useState("");
  const [socialHistory, setSocialHistory] = useState("");
  const [clinicalExam, setClinicalExam] = useState("");
  const [impression, setImpression] = useState("");

  // Diagnostic investigations
  const [fbc, setFbc] = useState("");
  const [ues, setUes] = useState("");
  const [lft, setLft] = useState("");
  const [ldh, setLdh] = useState("");
  const [uricAcid, setUricAcid] = useState("");
  const [viralSerology, setViralSerology] = useState("");
  const [echo, setEcho] = useState("");

  const SOAP_TYPES = [
    { value: "subjective", label: "Subjective" },
    { value: "objective", label: "Objective" },
    { value: "assessment", label: "Assessment" },
    { value: "plan", label: "Plan" },
  ];

  useEffect(() => {
    if (location?.state?.entryMode === "upload") {
      setEntryMode("upload");
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!patientId) {
      alert("Patient ID missing!");
      return;
    }

    if(title === "" || typeText === "" ) {
      toast.error("Please fill in all fields!");
      return;
    }

    // ---- PAYLOAD TO BACKEND ----
    const payload = {
      patient_id: patientId,
      title,
      type: typeText,
      history_presenting_complaints: histPresenting || null,
      past_medical_history: pastMedical || null,
      medication: medication || null,
      family_history: familyHistory || null,
      social_history: socialHistory || null,
      clinical_examination_findings: clinicalExam || null,
      clinical_impression: impression || null,
      full_blood_count: fbc || null,
      u_and_es: ues || null,
      lft: lft || null,
      ldh: ldh || null,
      uric_acid: uricAcid || null,
      viral_serology_results: viralSerology || null,
      echo: echo || null,
      additional_comments: content || null,
      doctor_id: user?.role === ROLES.SUPERADMIN ? null : (user?.id || null),
    };

    try {
      await createClinicalNote(payload);
      toast.success("Clinical note saved successfully!");
      navigate(`/patients/${patientId}?tab=clinical-notes`);
    } catch (err: any) {
      console.error("Failed to create note:", err);
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to save clinical note";
      toast.error(errorMessage);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Add Clinical Note</h1>
        <p className="text-gray-600">Patient ID: {patientId}</p>
      </div>

      {/* Entry mode selector */}
      <div className="card space-y-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Entry Mode
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setEntryMode("manual")}
            className={`${
              entryMode === "manual"
                ? "border-primary-500 bg-primary-50"
                : "border-gray-200 bg-white hover:bg-gray-50"
            } border rounded-lg p-4 text-left transition-colors`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p
                  className={`text-sm font-medium ${
                    entryMode === "manual"
                      ? "text-primary-700"
                      : "text-gray-900"
                  }`}
                >
                  Type manually
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Use the large text area to enter a detailed clinical note.
                </p>
              </div>
              <span
                className={`inline-flex h-5 w-5 rounded-full border ${
                  entryMode === "manual"
                    ? "border-primary-600 bg-primary-600"
                    : "border-gray-300 bg-white"
                }`}
              ></span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setEntryMode("upload")}
            className={`${
              entryMode === "upload"
                ? "border-primary-500 bg-primary-50"
                : "border-gray-200 bg-white hover:bg-gray-50"
            } border rounded-lg p-4 text-left transition-colors`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p
                  className={`text-sm font-medium ${
                    entryMode === "upload"
                      ? "text-primary-700"
                      : "text-gray-900"
                  }`}
                >
                  Upload document
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Attach a clinical note file and optionally add comments.
                </p>
              </div>
              <span
                className={`inline-flex h-5 w-5 rounded-full border ${
                  entryMode === "upload"
                    ? "border-primary-600 bg-primary-600"
                    : "border-gray-300 bg-white"
                }`}
              ></span>
            </div>
          </button>
        </div>
      </div>

      {/* FORM */}
      <form className="card space-y-4" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input-field mt-1"
              placeholder="e.g., Follow-up Visit"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Type 
            </label>
            <CustomSelect
              value={typeText}
              onChange={(val) => setTypeText(val)}
              options={SOAP_TYPES.map((t) => ({ value: t.value, label: t.label }))}
              placeholder="Select note type"
              className="mt-1"
            />
          </div>
        </div>

        {/* UPLOAD MODE */}
        {entryMode === "upload" && (
          <div
            className="border-2 border-dashed rounded-lg p-6 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
            onClick={() => {
              const el = document.getElementById(
                "note-file-input"
              ) as HTMLInputElement | null;
              el?.click();
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Upload Clinical Note
                </p>
                <p className="text-sm text-gray-600">
                  Drag & drop or click to select a file (PDF, DOC, DOCX, TXT)
                </p>
                {noteFile && (
                  <p className="text-sm text-primary-700 mt-2">
                    Selected: {noteFile.name} (
                    {Math.round(noteFile.size / 1024)} KB)
                  </p>
                )}
              </div>

              <button type="button" className="btn-primary">
                Choose File
              </button>
            </div>

            <input
              id="note-file-input"
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              className="hidden"
              onChange={(e) => setNoteFile(e.target.files?.[0] ?? null)}
            />
          </div>
        )}

        {/* STRUCTURED FIELDS */}
        <div className="space-y-4">
          <h3 className="text-md font-semibold text-gray-900">
            Structured Sections
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* History of the presenting complaints */}
            <div className="flex flex-col">
              <label className="font-medium mb-1">History of the presenting complaints</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={histPresenting}
                onChange={(e) => setHistPresenting(e.target.value)}
              />
            </div>

            {/* Past medical history */}
            <div className="flex flex-col">
              <label className="font-medium mb-1">Past medical history</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={pastMedical}
                onChange={(e) => setPastMedical(e.target.value)}
              />
            </div>

            {/* Medication */}
            <div className="flex flex-col">
              <label className="font-medium mb-1">Medication</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={medication}
                onChange={(e) => setMedication(e.target.value)}
              />
            </div>

            {/* Family history */}
            <div className="flex flex-col">
              <label className="font-medium mb-1">Family history</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={familyHistory}
                onChange={(e) => setFamilyHistory(e.target.value)}
              />
            </div>

            {/* Social history */}
            <div className="flex flex-col">
              <label className="font-medium mb-1">Social history</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={socialHistory}
                onChange={(e) => setSocialHistory(e.target.value)}
              />
            </div>

            {/* Clinical examination findings */}
            <div className="flex flex-col">
              <label className="font-medium mb-1">Clinical examination findings</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={clinicalExam}
                onChange={(e) => setClinicalExam(e.target.value)}
              />
            </div>

            {/* Clinical impression */}
            <div className="flex flex-col">
              <label className="font-medium mb-1">Clinical impression</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={impression}
                onChange={(e) => setImpression(e.target.value)}
              />
            </div>

          </div>
        </div>

        {/* DIAGNOSTIC INVESTIGATIONS */}
        <div className="space-y-4">
          <h3 className="text-md font-semibold text-gray-900">
            Diagnostic investigations
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Full blood count */}
            <div className="flex flex-col">
              <label className="font-medium mb-1">Full blood count</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={fbc}
                onChange={(e) => setFbc(e.target.value)}
              />
            </div>

            {/* U&Es */}
            <div className="flex flex-col">
              <label className="font-medium mb-1">U&Es</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={ues}
                onChange={(e) => setUes(e.target.value)}
              />
            </div>

            {/* LFT */}
            <div className="flex flex-col">
              <label className="font-medium mb-1">LFT</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={lft}
                onChange={(e) => setLft(e.target.value)}
              />
            </div>

            {/* LDH */}
            <div className="flex flex-col">
              <label className="font-medium mb-1">LDH</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={ldh}
                onChange={(e) => setLdh(e.target.value)}
              />
            </div>

            {/* Uric Acid */}
            <div className="flex flex-col">
              <label className="font-medium mb-1">Uric acid</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={uricAcid}
                onChange={(e) => setUricAcid(e.target.value)}
              />
            </div>

            {/* Viral serology results */}
            <div className="flex flex-col">
              <label className="font-medium mb-1">Viral serology results</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={viralSerology}
                onChange={(e) => setViralSerology(e.target.value)}
              />
            </div>

            {/* ECHO full width */}
            <div className="flex flex-col md:col-span-2">
              <label className="font-medium mb-1">ECHO</label>
              <textarea
                className="border rounded-md p-2 w-full h-28"
                value={echo}
                onChange={(e) => setEcho(e.target.value)}
              />
            </div>

            {/* Additional comments – full width + taller */}
            <div className="flex flex-col md:col-span-2">
              <label className="font-medium mb-1">Additional Comments</label>
              <textarea
                className="border rounded-md p-2 w-full"
                rows={10}
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>

          </div>
        </div>


        {/* ACTION BUTTONS */}
        <div className="flex justify-end space-x-3">
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Save Note
          </button>
        </div>

      </form>
      
    </div>
  );
};

export default AddClinicalNote;